import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Optional,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { createWorker } from 'tesseract.js';
import { Box, analyzeDocument, boxesClose, DocAnalysis } from './document-detect';
import { DocPrediction } from './document-classify';
import { coarseRotationDegrees, estimateSkewDegrees } from './document-orient';
import { DocModel } from './doc-model';
import { CnnClassifier } from './cnn-classifier';
import { DeviceCapabilityService } from '../core/services/device-capability.service';
import { CaptureWatchdogService } from '../core/services/capture-watchdog.service';
import { fileToCanvas } from '../shared/pdf';
import { Scanner } from '../scan/scanner';
import { canvasToPixels } from '../scan/browser-render';
import { ParsedDocument } from '../../lib/brdocs';
import { TESSERACT_CORE_PATH } from '../../lib/brdocs/browser/options';
import { playConfirmationBeep } from '../shared/beep';
import { CaptureDebugEvent } from '../shared/debug-event';
import { FileMetadata, FileIntakeResult } from './kyc-file-metadata.types';
import { intakeUploadedFile } from './kyc-file-intake';
import { intakeCameraCapture, CameraCaptureInfo } from './kyc-camera-metadata';
import { CameraStreamService } from './camera-stream.service';
import {
  RegionFocusController,
  focusAtPoint,
  supportsPointsOfInterest,
  tapToNormalizedPoint,
} from './camera-focus';

/** Feature-extraction resolution — must match the trainer's analysis width. */
const FEATURE_WIDTH = 480;

/**
 * Longest-edge cap (px) for a still kept from the **live camera** burst
 * (`grabFullFrame`). A live 1920×1080 stream is already under this, so real
 * camera captures pass through untouched; the cap exists mainly to keep peak
 * canvas memory in check on low-end phones, which grab a burst of
 * {@link CAPTURE_BURST} full-res stills per capture. The upload path uses a
 * separate, much larger cap ({@link MAX_UPLOAD_EDGE}) — see below.
 */
const MAX_CAPTURE_EDGE = 2600;
/**
 * Longest-edge cap (px) for the **file-upload** path (`processFile`). The
 * emitted still is the ONLY source for the CIN/CNH back-side signed-QR decode
 * (the verso `brdocs-capture` sets no `digitalFirst`, so the host's `lerQr`
 * decodes the emitted `rawDataUrl`) AND for the backend Textract CPF OCR, so it
 * must stay near-native: a high-res phone photo (e.g. 4032×3024) of a back keeps
 * its small-in-frame dense v40 QR / CPF text above the decode threshold.
 * Deliberately much larger than {@link MAX_CAPTURE_EDGE} — it only trims a truly
 * pathological render (e.g. a huge multi-megapixel PDF page). A one-shot upload
 * has none of the burst-canvas memory pressure the live path does, so there is
 * no reason to shrink it to camera size; peak memory is instead bounded by
 * releasing the big rendered canvas promptly (see `processFile`).
 */
const MAX_UPLOAD_EDGE = 5000;
/**
 * Longest-edge cap (px) for the image handed to the **on-device recognizer** (CNN
 * classify + brdocs preprocess). Deliberately far below {@link MAX_UPLOAD_EDGE}:
 * that pipeline runs synchronously on the main thread (onnx `numThreads=1`, no
 * proxy), so a multi-megapixel input froze the tab. Classification/localization
 * need only modest resolution; QR/OCR/storage keep the full-res image.
 */
const CLASSIFY_MAX_EDGE = 1400;
/** JPEG quality for the kept still (upload + OCR + QR source) — high, to keep QR/CPF legible. */
const CAPTURE_JPEG_QUALITY = 0.92;
/** Width of the down-scaled frame we analyse each tick (keeps the loop cheap). */
const ANALYSIS_WIDTH = 480;
/** Minimum gap between analyses — throttles the detector to ~8 frames/sec. */
const ANALYSIS_INTERVAL_MS = 120;
/** Consecutive good frames required before auto-capture fires (anti-jitter). */
const STABLE_FRAMES = 5;
/** How many candidate stills to grab in the burst before keeping the sharpest. */
const CAPTURE_BURST = 4;
/**
 * Default time the live detection loop may run without a successful capture before we
 * offer a manual escape hatch — a "Tirar foto" button + a spoken hint. Auto-capture
 * keeps running underneath; the button is a fallback, not a mode switch, and it is
 * hidden again the moment auto-capture recovers and fires.
 *
 * This is the DEFAULT only: the guided KYC flow overrides it per-action via the
 * {@link Docpic.manualFallbackMs} input (from `cp_actions.config.somosKyc.documentScanTimeSeconds`).
 * Standalone/kiosk usage with no host binding falls back to this value.
 */
const MANUAL_FALLBACK_MS = 7000;
/** Voiceover clip played once the manual-capture fallback appears (see generate-voiceovers.py). */
const MANUAL_FALLBACK_CLIP = 'captura-manual';
/** Per-word OCR confidence (0..100) at/above which a word counts as "clearly read". */
const OCR_WORD_CONFIDENCE = 60;
/** Min number of clearly-read words for the still to count as a legible document. */
const OCR_MIN_GOOD_WORDS = 6;
/** Max width we down-scale the cropped document to before OCR (speed vs. accuracy). */
const OCR_MAX_WIDTH = 1280;
/** Padding around the detected box when cropping for OCR, as a fraction of the frame. */
const OCR_CROP_PAD = 0.02;

/** What the detector currently thinks of the live frame, for UI + overlay. */
type Status =
  | 'idle'
  | 'searching'
  | 'too-small'
  | 'blurry'
  | 'not-document'
  | 'hold'
  | 'capturing'
  | 'verifying'
  | 'captured';

/** Result of the OCR legibility check on a captured still. */
interface OcrCheck {
  /** Total word-like tokens OCR returned. */
  words: number;
  /** Words read at/above {@link OCR_WORD_CONFIDENCE} (the legibility signal). */
  goodWords: number;
  /** Mean OCR confidence across all words (0..100), for reference. */
  confidence: number;
  legible: boolean;
  /** Full recognised text, used to classify the document type. */
  text: string;
}

/**
 * The fail-open OCR result — no text read, but deliberately `legible: true` so
 * legibility never blocks the flow. Used both when Tesseract itself fails and
 * when the heavy on-device stack is skipped on a weak device.
 */
const EMPTY_OCR_CHECK: OcrCheck = { words: 0, goodWords: 0, confidence: 0, legible: true, text: '' };

/**
 * Auto-capturing document camera. While running it continuously analyses the
 * webcam feed ({@link analyzeDocument}) to:
 *
 *  - **detect the document frame** and draw it as a live overlay,
 *  - **gate on crisp text** (a variance-of-Laplacian focus score),
 *  - **gate on text size** (too-small/too-far text can't be read), and
 *  - **confirm it is really a document** (paper-bright, ink present, and that
 *    ink spread across the page like text).
 *
 * Once those hold steady for {@link STABLE_FRAMES} frames it grabs a short burst
 * and keeps the sharpest still, then runs **OCR** on it (Tesseract.js, in a
 * worker) as the final legibility check: only if the text actually reads back
 * with enough confidence does it **beep** and keep the photo — otherwise it
 * resumes scanning for a better frame. **Retake** resumes the loop. A manual
 * **Take photo** button bypasses the gates. Everything is torn down in
 * {@link ngOnDestroy}.
 */

/** Emitted by `<brdocs-capture>` when a document is confirmed (auto-captured). */
export interface BrdocsCaptureResult {
  /** Document type key (e.g. 'rg-front'), or 'unknown' when not recognized. */
  type: string;
  label: string;
  confidence: 'high' | 'medium' | 'low';
  /** False when the model rejected it as out-of-distribution. */
  recognized: boolean;
  /** The rectified, upright image that was classified, as a PNG data URL. */
  processedDataUrl: string;
  /** Bytes of {@link processedDataUrl}. */
  processed: Uint8Array;
  /** The full OCR text read from the document (Portuguese), for field extraction. */
  ocrText: string;
  /** The raw captured still (JPEG data URL) before rectification, for display. */
  rawDataUrl: string;
  /** Raw source bytes to persist (original upload, or HEIC-converted / camera JPEG). */
  rawStorageBlob?: Blob;
  /** Filename whose extension matches rawStorageBlob (drives stored MIME). */
  rawStorageName?: string;
  /** Extracted file metadata (embedded or synthetic). */
  fileMetadata?: FileMetadata;
  /**
   * True when the classifier/OCR models failed to load or threw and this result
   * is a safe `unknown` fallback (emitted so the host doesn't dead-end) rather
   * than a genuine classification. Optional/additive: hosts that ignore it keep
   * working; a host can use it to route to a manual document-type picker.
   */
  classifyFailed?: boolean;
}

@Component({
  selector: 'app-docpic, brdocs-capture',
  templateUrl: './docpic.html',
  styleUrls: ['./docpic.scss'],
})
export class Docpic implements OnChanges, OnDestroy, AfterViewInit {
  @ViewChild('video') private video!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvas') private canvas!: ElementRef<HTMLCanvasElement>;

  /**
   * Kiosk mode: hide all of this component's own chrome (camera picker, buttons,
   * metrics, result panel) and auto-start the camera, leaving just the live
   * viewport + detection overlay. The host (e.g. the guided full-capture flow)
   * provides the friendly UI and reacts to {@link documentConfirmed}.
   */
  @Input() kiosk = false;

  /**
   * Where the document comes from in kiosk mode: `'camera'` opens the live
   * auto-capturing feed (default); `'upload'` skips the camera entirely and lets
   * the user pick an image/PDF from their device, running the same detection +
   * OCR + classify pipeline and emitting the same {@link documentConfirmed}.
   */
  @Input() source: 'camera' | 'upload' = 'camera';

  /**
   * Try a deterministic read **before** the CNN on uploads: decode the document's
   * signed QR/MRZ and, if a real Brazilian document comes through, identify it
   * from that (type + verified signature) instead of running the image through
   * the classifier. The host reacts via {@link digitalRecognized}. Opt-in so the
   * standalone debug tab still always runs the CNN.
   */
  @Input() digitalFirst = false;

  /**
   * Whether the manual-capture fallback speaks its spoken hint aloud. Mirrors the
   * guided flow's voice preference so a host with voice turned off stays silent;
   * defaults on so the standalone/kiosk usage is voiced without extra wiring.
   */
  @Input() voice = true;

  /**
   * How long (ms) the live detection loop may run without a successful capture before the
   * manual "Tirar foto" escape hatch appears. Defaults to {@link MANUAL_FALLBACK_MS}; the
   * guided KYC flow overrides it per-action (see the host's `documentScanTimeSeconds` config).
   * A non-positive/invalid value falls back to the default.
   */
  @Input() manualFallbackMs: number | undefined = MANUAL_FALLBACK_MS;

  private stream?: MediaStream;
  private rafId?: number;
  /** Off-screen canvas for the down-scaled analysis frame. */
  private analysisCanvas?: HTMLCanvasElement;
  /** Timestamp of the last analysis, for throttling. */
  private lastAnalysisAt = 0;
  /** Run of consecutive capture-ready frames, and the box they centred on. */
  private goodRun = 0;
  private lastBox: Box | null = null;
  /** Throttled region-driven autofocus driver (Tier 2). */
  private focusCtl = new RegionFocusController();
  /** Current tap-to-focus ring position (percent), or null when hidden (Tier 3). */
  protected focusRingPoint: { xPct: number; yPct: number } | null = null;
  private focusRingTimer?: ReturnType<typeof setTimeout>;
  /** True while the rAF detection loop is armed (separate from the camera). */
  private loopActive = false;
  /** True while collecting the candidate burst (suppresses re-triggering). */
  private capturing = false;
  /** Full-res candidate stills from the burst, with focus scores and doc box. */
  private candidates: { dataUrl: string; score: number; box: Box | null }[] = [];
  /** Lazily-created Tesseract worker for the OCR legibility check. */
  private ocrWorker?: Awaited<ReturnType<typeof createWorker>>;
  /** In-flight worker creation, so we only ever spin up one. */
  private ocrWorkerInit?: Promise<Awaited<ReturnType<typeof createWorker>>>;
  /** Raw bytes + metadata for the in-flight capture, consumed by emitConfirmed. */
  private pendingIntake: FileIntakeResult | null = null;

  /** Hard {@link MANUAL_FALLBACK_MS} timer that offers a manual shot if nothing auto-captures. */
  private manualFallbackTimer?: ReturnType<typeof setTimeout>;
  /** Reused <audio> element for the manual-fallback spoken hint. */
  private fallbackAudio?: HTMLAudioElement;
  /** Detaches the autoplay-retry gesture listeners, when armed. */
  private fallbackGestureCleanup?: () => void;
  /**
   * True once {@link MANUAL_FALLBACK_MS} elapsed with no successful capture: shows the
   * manual "Tirar foto" escape-hatch button. Cleared as soon as a capture happens.
   */
  protected manualFallbackVisible = false;

  protected cameras: MediaDeviceInfo[] = [];
  protected selectedCameraId: string | undefined = undefined;
  protected running = false;
  protected error: string | null = null;
  /**
   * True when the camera itself couldn't be opened (permission denied, no
   * device, in use, or insecure context) — drives the friendly error card and
   * its "send a photo instead" fallback, as opposed to a benign transient error.
   */
  protected cameraFailed = false;
  /** When on, the loop captures by itself; otherwise it only guides the overlay. */
  protected autoCapture = true;

  /** Live detector state, surfaced to the UI and the overlay. */
  protected status: Status = 'idle';
  protected analysis: DocAnalysis | null = null;
  protected overlayBox: Box | null = null;

  /** The most recent captured still as a JPEG data URL, if any. */
  protected photo: string | null = null;
  protected get hasPhoto(): boolean {
    return this.photo !== null;
  }
  /** Result of the OCR legibility check on the kept still, for display. */
  protected ocr: OcrCheck | null = null;
  /** Detected document type for the captured still, once identified. */
  protected docType: DocPrediction | null = null;
  /** True while identifying the document type (OCR + classify). */
  protected identifying = false;
  /** Inspection data from the CNN: the image actually fed to the model + scores. */
  protected get cnnDebug() {
    return this.cnn.lastDebug;
  }

  /**
   * Fires once per confirmed capture with the document type + processed-image
   * bytes — the `<brdocs-capture (documentConfirmed)="...">` output (use case 1b).
   */
  @Output() documentConfirmed = new EventEmitter<BrdocsCaptureResult>();

  /**
   * Fires instead of {@link documentConfirmed} when {@link digitalFirst} is on
   * and the upload's signed QR/MRZ decoded — carrying the parsed document
   * (fields + verified signature) and a display image.
   *
   * This reports **facts, not a verdict**. A SERPRO-signed QR proves the *data* is
   * authentic; it says nothing about what the *file* is. The physical CNH's inner
   * page and the CNH-e PDF carry the same signed payload, so a photo of the card's
   * QR page verifies exactly like a genuine export — and submitting that photo as a
   * lone document-front is what Veriff declines with 647 "Document not recognised".
   * So we hand the flow the two things that DO separate them — the container the
   * file arrived in, and (for images) what the CNN makes of it — and let
   * `full-capture` route. See docs/superpowers/specs/2026-07-12-cnh-e-provenance-routing-design.md.
   */
  @Output() digitalRecognized = new EventEmitter<{
    doc: ParsedDocument;
    dataUrl: string;
    /** The container the file arrived in. A genuine CNH-e export is a `pdf`. */
    source: 'pdf' | 'image';
    /**
     * CNN class of an uploaded image (`cnh-digital` = a picture of a digital CNH;
     * `cnh-front`/`cnh-open` = the physical card). Null for PDFs (not classified) and
     * when the on-device model isn't warm enough to answer.
     */
    cnnType: string | null;
    rawStorageBlob?: Blob;
    rawStorageName?: string;
    fileMetadata?: FileMetadata;
  }>();

  /** Low-level diagnostics (camera, OCR, classification, model load, errors). */
  @Output() debug = new EventEmitter<CaptureDebugEvent>();

  /**
   * Fires when the user abandons the camera for the file-upload path (the
   * "send a photo instead" fallback). A kiosk host that binds `[source]` one-way
   * must react to this and flip its own method state to `'upload'`, otherwise the
   * parent stays in camera mode and `[source]`/`[digitalFirst]` no longer match.
   * Optional/additive: standalone/kiosk-less usages that don't bind it still work
   * (this component also flips its own {@link source} locally in that case).
   */
  @Output() switchToUpload = new EventEmitter<void>();

  constructor(
    private docModel: DocModel,
    private cnn: CnnClassifier,
    private scanner: Scanner,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private deviceCapability: DeviceCapabilityService,
    private watchdog: CaptureWatchdogService,
    @Optional() private camera?: CameraStreamService,
  ) {}

  /** Emit one diagnostic event. */
  private dbg(
    level: CaptureDebugEvent['level'],
    category: string,
    message: string,
    data?: Record<string, unknown>,
  ): void {
    this.debug.emit({ level, category, message, data });
  }

  /**
   * Emit the Tesseract OCR result on the debug stream — counts plus the raw
   * recognised `text`, so consumers can see exactly what OCR read. Called from
   * every OCR path (live legibility gate, file upload, manual grab).
   */
  private logOcr(check: OcrCheck, message: string): void {
    this.dbg('info', 'ocr', message, {
      goodWords: check.goodWords,
      words: check.words,
      confidence: Math.round(check.confidence),
      legible: check.legible,
      text: check.text.trim(),
    });
  }

  /** Human-readable hint shown over the viewport. */
  protected get statusText(): string {
    return STATUS_TEXT[this.status];
  }
  /** Overlay frame colour reflects how close we are to a capture. */
  protected get overlayState(): 'searching' | 'blurry' | 'ready' {
    const s = this.status;
    if (s === 'hold' || s === 'capturing' || s === 'verifying' || s === 'captured') return 'ready';
    if (s === 'blurry' || s === 'not-document' || s === 'too-small') return 'blurry';
    return 'searching';
  }

  /**
   * True while the captured frame is being processed — grabbing the burst,
   * reading the text (OCR), or identifying the type (classify). Drives the
   * "thinking" overlay so the user knows the system is working, even when the
   * first read is slow because the OCR/model files are still downloading.
   */
  protected get processing(): boolean {
    const s = this.status;
    return this.identifying || s === 'capturing' || s === 'verifying';
  }
  /** Label for the processing overlay. */
  protected get processingText(): string {
    return this.identifying ? 'Lendo o documento…' : 'Processando…';
  }

  /** In kiosk camera mode, open the camera automatically once the view exists. */
  ngAfterViewInit(): void {
    // Start warming the heavy models as soon as the capture screen mounts, for BOTH
    // camera and upload (start() also warms; the call is idempotent). Without this
    // the upload path's first classify does a cold onnx/WebGPU load on the main
    // thread and freezes the tab (see prewarmHeavyModels + the skip-if-cold gate).
    this.prewarmHeavyModels();
    if (this.kiosk && this.source === 'camera') void this.start();
  }

  /** Enumerate available cameras (labels require an active permission grant). */
  async loadCameras(): Promise<void> {
    this.error = null;
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cams = devices.filter((d) => d.kind === 'videoinput');
      this.cameras = cams;
      if (cams.length && !this.selectedCameraId) {
        this.selectedCameraId = cams[0].deviceId;
      }
    } catch (e) {
      this.cameraFailed = true;
      this.error = this.cameraErrorMessage(e);
    }
  }

  /**
   * Map a `getUserMedia`/device error to a friendly, translated message. Covers
   * the common `DOMException` names plus the insecure-context / unsupported case.
   */
  private cameraErrorMessage(e: unknown): string {
    const name = (e as { name?: string } | null)?.name ?? '';
    switch (name) {
      case 'NotAllowedError':
      case 'PermissionDeniedError':
        return this.translate.instant('CameraPermissionDenied');
      case 'NotFoundError':
      case 'DevicesNotFoundError':
      case 'OverconstrainedError':
        return this.translate.instant('CameraNotFound');
      case 'NotReadableError':
      case 'TrackStartError':
        return this.translate.instant('CameraNotReadable');
      case 'SecurityError':
        return this.translate.instant('CameraInsecureContext');
      default:
        return this.translate.instant('CameraGenericError');
    }
  }

  /**
   * Abandon the camera and switch to the file-upload path instead — the #5
   * fallback when the camera can't be opened. Flips this component's own
   * `source` to `'upload'` (so the standalone/kiosk-less usage still renders the
   * upload button) AND notifies the host via {@link switchToUpload}. A kiosk host
   * binds `[source]` one-way, so the local flip alone would leave the parent in
   * camera mode; the host must react to the event and set its own method state.
   * The uploaded file runs the identical detect/OCR/classify pipeline and emits
   * the same {@link documentConfirmed}.
   */
  protected useUploadInstead(): void {
    this.error = null;
    this.cameraFailed = false;
    this.source = 'upload';
    this.dbg('info', 'camera', 'user switched to upload after camera failure');
    this.switchToUpload.emit();
    try { this.cdr.detectChanges(); } catch {}
  }

  /** Label for the "send a photo instead" fallback button. */
  protected get uploadInsteadLabel(): string {
    return this.translate.instant('SendPhotoInstead');
  }
  /** Label for the "try again" retry button on the camera-error card. */
  protected get tryAgainLabel(): string {
    return this.translate.instant('CameraTryAgain');
  }

  /** Open the camera, show the live feed, and start the detection loop. */
  /** Guards {@link prewarmHeavyModels} so the (idempotent) load only fires once. */
  private prewarmStarted = false;

  /**
   * Warm the heavy on-device models (CNN recognizer + OCR worker) in the background,
   * each within a load budget. This exists because a COLD `cnn.classify` builds the
   * onnx/WebGPU sessions synchronously on the main thread (~10s WebGPU pipeline
   * compile) — the confirmed cause of the frozen tab after the back-of-document
   * upload. The camera path warmed these during framing; the upload path never did.
   * Idempotent (the recognizer caches its load promise). No-op on a weak device.
   */
  private prewarmHeavyModels(): void {
    if (this.prewarmStarted) return;
    this.prewarmStarted = true;
    if (!this.deviceCapability.evaluate()) {
      this.dbg('info', 'model', 'heavy on-device processing disabled (weak device); skipping model pre-warm');
      return;
    }
    void this.deviceCapability.guardLoad(() => this.ocrReady()).then((w) =>
      this.dbg('info', 'model', w ? 'OCR worker ready' : 'OCR worker skipped (weak device / budget)'),
    );
    void this.deviceCapability.guardLoad(() => this.cnn.load()).then((ok) =>
      this.dbg('info', 'model', ok ? 'CNN model loaded' : 'CNN unavailable (fallback / skip)', {
        backend: this.cnn.backend,
      }),
    );
  }

  async start(): Promise<void> {
    this.error = null;
    this.cameraFailed = false;
    this.photo = null;
    this.docType = null;
    // Insecure origin (non-HTTPS) or an unsupported browser: getUserMedia is
    // missing entirely — surface a clear message rather than a raw TypeError.
    if (!navigator.mediaDevices?.getUserMedia) {
      this.running = false;
      this.cameraFailed = true;
      this.error = this.translate.instant('CameraInsecureContext');
      this.dbg('error', 'camera', 'getUserMedia unavailable (insecure context or unsupported)');
      try { this.cdr.detectChanges(); } catch {}
      return;
    }
    try {
      const deviceId = this.selectedCameraId;
      // One shared stream for the whole flow when the CameraStreamService is
      // present (guided capture); otherwise a direct high-res stream (standalone).
      if (this.camera) {
        this.stream = await this.camera.acquire({ deviceId, facingMode: 'environment' });
      } else {
        const hires = { width: { ideal: 1920 }, height: { ideal: 1080 } };
        this.stream = await navigator.mediaDevices.getUserMedia({
          video: deviceId
            ? { deviceId: { exact: deviceId }, ...hires }
            : { facingMode: 'environment', ...hires },
          audio: false,
        });
      }
      const video = this.video.nativeElement;
      video.srcObject = this.stream;
      await video.play();
      this.running = true;
      this.dbg('info', 'camera', 'camera started');
      this.resetDetection();
      // Warm the heavy on-device stack in the background while the user frames the
      // document (also run for the upload path — see prewarmHeavyModels).
      this.prewarmHeavyModels();
      await this.loadCameras();
      this.startLoop();
      this.startManualFallback();
      try { this.cdr.detectChanges(); } catch {}
    } catch (e) {
      this.running = false;
      this.cameraFailed = true;
      this.error = this.cameraErrorMessage(e);
      this.dbg('error', 'camera', 'camera failed to open', {
        error: String(e),
        name: (e as { name?: string } | null)?.name,
      });
      try { this.cdr.detectChanges(); } catch {}
    }
  }

  /** Arm the rAF detection loop. */
  private startLoop(): void {
    this.loopActive = true;
    this.rafId = requestAnimationFrame(this.tick);
  }

  /**
   * Arm the manual-capture fallback: {@link MANUAL_FALLBACK_MS} after the live
   * detection loop starts (camera ready), if nothing has auto-captured yet, reveal
   * a manual "Tirar foto" button and speak a short hint. Auto-capture keeps running
   * — {@link clearManualFallback} cancels the timer / hides the button the moment a
   * capture succeeds (auto or manual) or the camera stops.
   */
  private startManualFallback(): void {
    this.clearManualFallback();
    // Guard against a non-positive/invalid host override — fall back to the default.
    const delayMs =
      typeof this.manualFallbackMs === 'number' && this.manualFallbackMs > 0
        ? this.manualFallbackMs
        : MANUAL_FALLBACK_MS;
    this.manualFallbackTimer = setTimeout(() => {
      this.manualFallbackTimer = undefined;
      // Only surface the escape hatch if we're still scanning with nothing captured.
      if (!this.running || this.hasPhoto) return;
      this.manualFallbackVisible = true;
      this.dbg('info', 'capture', 'auto-capture timed out; offering manual shot', { delayMs });
      if (this.voice) this.playFallbackVoiceover();
      try { this.cdr.detectChanges(); } catch {}
    }, delayMs);
  }

  /**
   * Re-arm the manual-capture fallback when the host supplies/updates {@link manualFallbackMs}
   * AFTER the timer was already armed — the guided KYC flow resolves the value asynchronously
   * from `cp_actions.config`, so it can land just after the camera loop started. Only re-arms
   * while still actively scanning with the timer pending and nothing captured yet; it never
   * resurrects the button after a capture or once it has already appeared.
   */
  ngOnChanges(changes: SimpleChanges): void {
    const change = changes['manualFallbackMs'];
    if (!change || change.firstChange) return;
    if (
      this.running &&
      !this.hasPhoto &&
      !this.manualFallbackVisible &&
      this.manualFallbackTimer !== undefined
    ) {
      this.startManualFallback();
    }
  }

  /** Cancel the fallback timer, hide the button, and stop its spoken hint (idempotent). */
  private clearManualFallback(): void {
    if (this.manualFallbackTimer !== undefined) {
      clearTimeout(this.manualFallbackTimer);
      this.manualFallbackTimer = undefined;
    }
    this.manualFallbackVisible = false;
    this.stopFallbackVoiceover();
  }

  /**
   * Play the manual-fallback spoken hint (the pre-recorded neural clip). Best-effort:
   * a missing file stays silent (no robotic speechSynthesis fallback), and an
   * autoplay block re-arms the clip to play on the next user gesture.
   */
  private playFallbackVoiceover(): void {
    try {
      const el = (this.fallbackAudio ??= new Audio());
      el.onerror = null; // missing file → silent
      el.src = `assets/brdocs/audio/${MANUAL_FALLBACK_CLIP}.mp3`;
      el.currentTime = 0;
      void el.play().catch(() => this.armFallbackGesture());
    } catch {
      // silent — the on-screen button still guides the user
    }
  }

  /** Retry the blocked fallback clip once, on the next pointer/key/touch, then detach. */
  private armFallbackGesture(): void {
    if (this.fallbackGestureCleanup) return;
    const retry = () => {
      this.fallbackGestureCleanup?.();
      if (this.manualFallbackVisible) this.fallbackAudio?.play().catch(() => {});
    };
    const remove = () => {
      this.fallbackGestureCleanup = undefined;
      window.removeEventListener('pointerdown', retry);
      window.removeEventListener('keydown', retry);
      window.removeEventListener('touchstart', retry);
    };
    this.fallbackGestureCleanup = remove;
    window.addEventListener('pointerdown', retry, { once: true });
    window.addEventListener('keydown', retry, { once: true });
    window.addEventListener('touchstart', retry, { once: true });
  }

  /** Stop the fallback spoken hint and drop any armed autoplay-retry listeners. */
  private stopFallbackVoiceover(): void {
    this.fallbackGestureCleanup?.();
    this.fallbackGestureCleanup = undefined;
    this.fallbackAudio?.pause();
  }

  /**
   * The detection loop. Throttled to {@link ANALYSIS_INTERVAL_MS}, it draws the
   * frame into a small canvas, analyses it, updates the overlay/status, and —
   * when auto-capture is on and the frame has been good and steady for
   * {@link STABLE_FRAMES} — collects a short candidate burst and keeps the
   * sharpest. {@link loopActive} (not `running`) gates the loop so a capture can
   * stop scanning while leaving the camera open for a retake.
   */
  private tick = (): void => {
    if (!this.loopActive) return;
    const now = performance.now();
    const video = this.video.nativeElement;
    if (
      now - this.lastAnalysisAt >= ANALYSIS_INTERVAL_MS &&
      video.readyState === video.HAVE_ENOUGH_DATA
    ) {
      this.lastAnalysisAt = now;
      this.analyzeCurrentFrame(video);
    }
    if (this.loopActive) {
      this.rafId = requestAnimationFrame(this.tick);
    }
  };

  /** Analyse the current video frame and advance the capture state machine. */
  private analyzeCurrentFrame(video: HTMLVideoElement): void {
    const img = this.grabAnalysisFrame(video);
    if (!img) return;
    const res = analyzeDocument(img);
    this.analysis = res;
    this.overlayBox = res.box;
    this.driveRegionFocus(res);

    // Already committed: keep grabbing candidate stills until the burst is full.
    if (this.capturing) {
      this.collectCandidate(res);
      try { this.cdr.detectChanges(); } catch {}
      return;
    }

    const ready = res.found && res.isDocument && res.legible && res.crisp;
    this.status =
      !res.found ? 'searching'
      : !res.isDocument ? 'not-document'
      : !res.legible ? 'too-small'
      : !res.crisp ? 'blurry'
      : 'hold';

    // Stability gate: the document must stay readable and roughly still.
    if (ready && res.box && (!this.lastBox || boxesClose(res.box, this.lastBox))) {
      this.goodRun++;
    } else {
      this.goodRun = ready ? 1 : 0;
    }
    this.lastBox = res.box;

    if (this.autoCapture && this.goodRun >= STABLE_FRAMES) {
      this.capturing = true;
      this.candidates = [];
      this.status = 'capturing';
      this.collectCandidate(res);
    }
    try { this.cdr.detectChanges(); } catch {}
  }

  /**
   * Tier 2 — region-driven autofocus. Point the lens at the detected document
   * (the colour-mask box `analyzeDocument` already gives us) while the frame is
   * not yet crisp. Throttled by {@link RegionFocusController} so it nudges rather
   * than hunts, and feature-detected so it is a no-op where the browser can't
   * steer focus (e.g. iOS Safari).
   */
  private driveRegionFocus(res: DocAnalysis): void {
    const track = this.camera?.videoTrack() ?? this.stream?.getVideoTracks?.()[0];
    if (!track || !supportsPointsOfInterest(track)) return;
    const p = this.focusCtl.next(res.box, res.crisp, res.found && !res.crisp, performance.now());
    if (p) void focusAtPoint(track, p.x, p.y);
  }

  /**
   * Grab one full-resolution still for the current burst, scored by its focus
   * value and tagged with the document box. Once {@link CAPTURE_BURST} are in
   * hand, keep the sharpest and hand it to the OCR legibility check.
   */
  private collectCandidate(res: DocAnalysis): void {
    const shot = this.grabFullFrame();
    if (shot) {
      this.candidates.push({ dataUrl: shot, score: res.sharpness, box: res.box });
    }
    if (this.candidates.length >= CAPTURE_BURST) {
      const best = this.candidates.reduce((a, b) => (b.score > a.score ? b : a));
      this.candidates = [];
      this.capturing = false;
      // Pause scanning while OCR runs; verifyLegibility resumes it on failure.
      this.stopLoop();
      this.status = 'verifying';
      void this.verifyLegibility(best.dataUrl, best.box);
    }
  }

  /**
   * The pre-beep gate: OCR the sharpest still — *cropped to the detected
   * document* so background clutter doesn't poison the read — and keep it (and
   * beep) only if enough words come back at high per-word confidence, proof it
   * is both a document and legible. A weak read is discarded and scanning
   * resumes. If OCR itself fails (e.g. offline) we fall back to keeping the
   * sharp still so the feature still works.
   */
  private async verifyLegibility(dataUrl: string, box: Box | null): Promise<void> {
    let check: OcrCheck;
    let featImage: ImageData | null = null;
    try {
      ({ check, featImage } = await this.ocrUpright(dataUrl, box));
    } catch {
      // OCR unavailable → accept so the feature still works.
      check = { words: 0, goodWords: 0, confidence: 0, legible: true, text: '' };
    }
    if (!this.running) return; // stopped / torn down while OCR ran

    this.logOcr(check, check.legible ? 'legible capture' : 'capture too faint, retrying');

    if (check.legible) {
      // Auto-capture recovered/succeeded: retire the manual escape hatch.
      this.clearManualFallback();
      this.ocr = check;
      this.photo = dataUrl;
      await this.setCameraIntake(dataUrl, this.video.nativeElement.videoWidth, this.video.nativeElement.videoHeight);
      this.status = 'captured';
      playConfirmationBeep();
      void this.classifyAndShow(featImage, check.text, dataUrl);
    } else {
      this.ocr = check;
      this.resetDetection();
      this.startLoop();
    }
    try { this.cdr.detectChanges(); } catch {}
  }

  /**
   * Crop to the document, de-rotate it upright (geometric 90° fix + a 0°/180°
   * flip resolved by whichever OCRs better), score legibility, and also return
   * the upright feature image — so OCR text and features match the model's
   * training. Mirrors the offline harness's `uprightCropAndOcr`.
   */
  private async ocrUpright(
    dataUrl: string,
    box: Box | null,
  ): Promise<{ check: OcrCheck; featImage: ImageData | null }> {
    // Skip the heavy Tesseract OCR entirely when this device can't run it — the
    // capture still proceeds to the provider with no OCR text (fail-open).
    if (!this.deviceCapability.heavyProcessingEnabled) {
      return { check: EMPTY_OCR_CHECK, featImage: null };
    }
    const worker = await this.ocrReady();
    const crop = await this.cropForOcr(dataUrl, box);
    const deg = coarseRotationDegrees(this.canvasPixels(crop, ANALYSIS_WIDTH));
    let up = deg ? this.rotateCanvas(crop, deg) : crop;
    // Fine de-skew: straighten the residual tilt so text rows are level (kept
    // in-place so the aspect ratio is unchanged) — helps OCR and the layout.
    const skew = estimateSkewDegrees(this.canvasPixels(up, ANALYSIS_WIDTH));
    if (Math.abs(skew) >= 0.5) up = this.rotateCanvas(up, skew);
    const flipped = this.rotateCanvas(up, 180);
    // Backstop: if the two OCR passes overrun the per-capture budget, skip them
    // and proceed without text (this also latches heavy processing off so later
    // captures on this slow device don't wait again).
    const recognized = await this.deviceCapability.guardCapture(() =>
      Promise.all([
        worker.recognize(up, {}, { blocks: true }),
        worker.recognize(flipped, {}, { blocks: true }),
      ]),
    );
    if (!recognized) return { check: EMPTY_OCR_CHECK, featImage: null };
    const [r0, r1] = recognized;
    const c0 = this.toCheck(r0.data);
    const c1 = this.toCheck(r1.data);
    const useFlip = c1.goodWords > c0.goodWords;
    const uprightCanvas = useFlip ? flipped : up;
    return {
      check: useFlip ? c1 : c0,
      featImage: this.canvasPixels(uprightCanvas, FEATURE_WIDTH),
    };
  }

  /** Build an {@link OcrCheck} from one Tesseract result. */
  private toCheck(data: { text: string; confidence: number; blocks: Parameters<typeof collectWords>[0] }): OcrCheck {
    const allWords = collectWords(data.blocks);
    const words = allWords.filter((w) => w.text.trim().length >= 2).length;
    const goodWords = allWords.filter(
      (w) => w.confidence >= OCR_WORD_CONFIDENCE && w.text.trim().length >= 2,
    ).length;
    return { words, goodWords, confidence: data.confidence, legible: goodWords >= OCR_MIN_GOOD_WORDS, text: data.text };
  }

  /**
   * Rotate a canvas. Multiples of 90° swap dimensions exactly (coarse
   * orientation / flip); any other angle is a fine de-skew that keeps the canvas
   * size (aspect unchanged) and white-fills the small exposed corners.
   */
  private rotateCanvas(src: HTMLCanvasElement, deg: number): HTMLCanvasElement {
    if (deg % 360 === 0) return src;
    const rad = (deg * Math.PI) / 180;
    const square = deg % 90 === 0;
    const swap = deg % 180 !== 0;
    const c = document.createElement('canvas');
    c.width = square && swap ? src.height : src.width;
    c.height = square && swap ? src.width : src.height;
    const ctx = c.getContext('2d');
    if (!ctx) return src;
    if (!square) {
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, c.width, c.height);
    }
    ctx.translate(c.width / 2, c.height / 2);
    ctx.rotate(rad);
    ctx.drawImage(src, -src.width / 2, -src.height / 2);
    return c;
  }

  /** Down-scale a canvas to at most `maxW` wide and read its pixels. */
  private canvasPixels(src: HTMLCanvasElement, maxW: number): ImageData {
    const w = Math.min(maxW, src.width);
    const h = Math.max(1, Math.round((w / src.width) * src.height));
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    const ctx = c.getContext('2d', { willReadFrequently: true })!;
    ctx.drawImage(src, 0, 0, w, h);
    return ctx.getImageData(0, 0, w, h);
  }

  /** Get the OCR worker, creating (and reusing) a single instance. */
  private ocrReady(): Promise<Awaited<ReturnType<typeof createWorker>>> {
    if (this.ocrWorker) return Promise.resolve(this.ocrWorker);
    // Portuguese — these are Brazilian documents; improves the read and the
    // accented keywords the classifier matches on. Pin the stable SIMD core so
    // tesseract doesn't auto-pick the relaxed-SIMD build that fails in some
    // Firefox releases (see TESSERACT_CORE_PATH).
    this.ocrWorkerInit ??= createWorker('por', undefined, {
      corePath: TESSERACT_CORE_PATH,
    }).then((w) => (this.ocrWorker = w));
    return this.ocrWorkerInit;
  }

  /**
   * Crop a candidate data URL to its detected document box (with a little
   * padding), then cap the width at {@link OCR_MAX_WIDTH}. Cropping out the
   * background is what lets Tesseract report a confident read. Falls back to the
   * whole frame when no box is known.
   */
  private async cropForOcr(dataUrl: string, box: Box | null): Promise<HTMLCanvasElement> {
    const img = new Image();
    img.src = dataUrl;
    await img.decode();
    const fw = img.naturalWidth;
    const fh = img.naturalHeight;
    const p = OCR_CROP_PAD;
    const sx = box ? Math.max(0, (box.x - p) * fw) : 0;
    const sy = box ? Math.max(0, (box.y - p) * fh) : 0;
    const sw = box ? Math.min(fw - sx, (box.w + 2 * p) * fw) : fw;
    const sh = box ? Math.min(fh - sy, (box.h + 2 * p) * fh) : fh;
    const scale = Math.min(1, OCR_MAX_WIDTH / sw);
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(sw * scale));
    canvas.height = Math.max(1, Math.round(sh * scale));
    canvas.getContext('2d')?.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
    return canvas;
  }

  /** Draw the live frame, down-scaled, into the reusable analysis canvas. */
  private grabAnalysisFrame(video: HTMLVideoElement): ImageData | null {
    return this.analysisImageData(video, video.videoWidth, video.videoHeight);
  }

  /**
   * Down-scale any image source to {@link ANALYSIS_WIDTH} and read its pixels —
   * shared by the live loop (video) and the file upload path (rendered canvas).
   */
  private analysisImageData(source: CanvasImageSource, sw: number, sh: number): ImageData | null {
    if (!sw || !sh) return null;
    const w = Math.min(ANALYSIS_WIDTH, sw);
    const h = Math.round((w / sw) * sh);
    const canvas = (this.analysisCanvas ??= document.createElement('canvas'));
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(source, 0, 0, w, h);
    return ctx.getImageData(0, 0, w, h);
  }

  /** Manually grab the current frame (bypasses the auto-capture gate). */
  async capture(): Promise<void> {
    const video = this.video.nativeElement;
    if (video.readyState !== video.HAVE_ENOUGH_DATA) return;
    const shot = this.grabFullFrame();
    if (!shot) return;
    this.clearManualFallback();
    this.capturing = false;
    this.candidates = [];
    this.ocr = null; // manual grab skips the legibility gate
    this.docType = null;
    this.photo = shot;
    await this.setCameraIntake(shot, video.videoWidth, video.videoHeight);
    this.status = 'captured';
    playConfirmationBeep();
    this.stopLoop();
    // Still identify the type — OCR + classify runs in the background.
    void this.identify(shot, this.lastBox);
    try { this.cdr.detectChanges(); } catch {}
  }

  /** Grab the current video frame as a down-scaled JPEG data URL (see #15). */
  private grabFullFrame(): string | null {
    const video = this.video.nativeElement;
    return this.encodeCappedStill(
      video,
      video.videoWidth,
      video.videoHeight,
      MAX_CAPTURE_EDGE,
      this.canvas.nativeElement,
    );
  }

  /**
   * The down-scaling gate for a kept still: draw `source` into a canvas whose
   * longest edge is capped at `maxEdge` and return it as a JPEG at
   * {@link CAPTURE_JPEG_QUALITY}. The live burst passes {@link MAX_CAPTURE_EDGE}
   * (bounds peak memory across the burst on low-end phones); the upload path
   * passes the much larger {@link MAX_UPLOAD_EDGE} so the emitted QR/OCR image
   * stays near-native. Reuses the supplied canvas when given (the live path
   * reuses {@link canvas}); otherwise allocates a throw-away one.
   */
  private encodeCappedStill(
    source: CanvasImageSource,
    sw: number,
    sh: number,
    maxEdge: number,
    canvas?: HTMLCanvasElement,
  ): string | null {
    if (!sw || !sh) return null;
    const scale = Math.min(1, maxEdge / Math.max(sw, sh));
    const w = Math.max(1, Math.round(sw * scale));
    const h = Math.max(1, Math.round(sh * scale));
    const c = canvas ?? document.createElement('canvas');
    c.width = w;
    c.height = h;
    const ctx = c.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(source, 0, 0, w, h);
    return c.toDataURL('image/jpeg', CAPTURE_JPEG_QUALITY);
  }

  /** Discard the captured still and resume scanning. */
  retake(): void {
    this.photo = null;
    this.ocr = null;
    this.docType = null;
    if (this.running) {
      this.resetDetection();
      this.startLoop();
      this.startManualFallback();
    }
    try { this.cdr.detectChanges(); } catch {}
  }

  /** Stop the camera and detection loop (idempotent). */
  stop(): void {
    this.running = false;
    this.stopLoop();
    this.clearManualFallback();
    this.focusCtl.reset();
    if (this.camera) {
      // The service owns the shared stream's lifetime — only detach it here so
      // the next step re-attaches the SAME stream (no reopen / re-permission).
      const v = this.video?.nativeElement;
      if (v) v.srcObject = null;
    } else {
      this.stream?.getTracks().forEach((t) => t.stop());
    }
    this.stream = undefined;
    this.status = 'idle';
    this.overlayBox = null;
    this.analysis = null;
    this.docType = null;
    try { this.cdr.detectChanges(); } catch {}
  }

  /** Tier 3 — tap-to-focus: point the lens where the user tapped + flash a ring. */
  protected onPreviewTap(ev: MouseEvent): void {
    const el = ev.currentTarget as HTMLElement;
    const rect = el.getBoundingClientRect();
    const p = tapToNormalizedPoint(rect, ev.clientX, ev.clientY);
    this.focusRingPoint = { xPct: p.x * 100, yPct: p.y * 100 };
    if (this.focusRingTimer) clearTimeout(this.focusRingTimer);
    this.focusRingTimer = setTimeout(() => {
      this.focusRingPoint = null;
      try { this.cdr.detectChanges(); } catch {}
    }, 700);
    const track = this.camera?.videoTrack() ?? this.stream?.getVideoTracks?.()[0];
    if (track) void focusAtPoint(track, p.x, p.y);
    try { this.cdr.detectChanges(); } catch {}
  }

  /** Cancel just the rAF loop, leaving the camera open (used after a capture). */
  private stopLoop(): void {
    this.loopActive = false;
    if (this.rafId !== undefined) {
      cancelAnimationFrame(this.rafId);
      this.rafId = undefined;
    }
  }

  private resetDetection(): void {
    this.goodRun = 0;
    this.lastBox = null;
    this.lastAnalysisAt = 0;
    this.capturing = false;
    this.candidates = [];
    this.status = 'searching';
  }

  /** Collect best-effort settings for the active camera track. */
  private cameraCaptureInfo(width: number, height: number): CameraCaptureInfo {
    let facingMode: string | undefined;
    let deviceLabel: string | undefined;
    let frameRate: number | undefined;
    try {
      const track = (this.stream?.getVideoTracks?.() ?? [])[0];
      const s: MediaTrackSettings = track?.getSettings?.() ?? {};
      facingMode = s.facingMode;
      frameRate = s.frameRate;
      deviceLabel = track?.label || undefined;
    } catch {
      /* best-effort */
    }
    return { width, height, facingMode, deviceLabel, frameRate, capturedAt: new Date() };
  }

  /** Build pendingIntake for a live capture (synthetic EXIF), fail-open. */
  private async setCameraIntake(dataUrl: string, width: number, height: number): Promise<void> {
    try {
      const blob = await (await fetch(dataUrl)).blob();
      this.pendingIntake = await intakeCameraCapture(
        blob,
        this.cameraCaptureInfo(width, height),
        'kyc-camera-capture.jpg',
      );
    } catch {
      this.pendingIntake = null;
    }
  }

  /**
   * Process an uploaded image or PDF through the same pipeline as a capture:
   * render it (PDF → first page), detect the document, then OCR + classify. The
   * camera is released first; unlike live capture there is no legibility gate —
   * we always show the result and surface what the read found.
   */
  async processFile(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    this.stop();
    this.error = null;
    this.photo = null;
    this.ocr = null;
    this.docType = null;
    this.analysis = null;
    this.identifying = true;
    // Breadcrumb for the freeze watchdog: if the tab locks up during this upload's
    // synchronous on-device work, the stall is attributed to `upload_process`.
    this.watchdog.extendContext({ heavyEnabled: this.deviceCapability.heavyProcessingEnabled, backend: this.cnn.backend });
    void this.watchdog.setPhase('upload_process', { sizeKb: Math.round(file.size / 1024), mime: file.type || null });
    // The full-resolution rendered page (esp. a multi-megapixel PDF) can be
    // large; hold a reference so we can release it promptly in `finally` (#15).
    let bigCanvas: HTMLCanvasElement | null = null;
    try {
      // KYC intake: read metadata from the ORIGINAL bytes and normalize to a
      // displayable image (HEIC->JPEG) BEFORE the canvas. Fail-open.
      let intake: FileIntakeResult | null = null;
      try {
        intake = await intakeUploadedFile(file);
      } catch {
        intake = null;
      }
      this.pendingIntake = intake;
      const canvasInput: Blob = intake?.displayBlob ?? file;
      const canvas = await fileToCanvas(canvasInput);
      bigCanvas = canvas;
      // Keep the upload near-native (only MAX_UPLOAD_EDGE bounds a pathological
      // render) — this emitted image is the QR/OCR source, so shrinking it to
      // camera size would drop a small dense back-side QR / CPF below the decode
      // threshold. Unlike the live burst, a one-shot upload has no burst-canvas
      // memory pressure. (The digitalFirst QR below reads the full-res canvas.)
      const dataUrl =
        this.encodeCappedStill(canvas, canvas.width, canvas.height, MAX_UPLOAD_EDGE) ??
        canvas.toDataURL('image/jpeg', CAPTURE_JPEG_QUALITY);

      // Digital-first: a signed QR/MRZ identifies the document deterministically.
      // Only a real brdocs parser counts — the generic 'text' fallback (any random
      // QR) does not.
      if (this.digitalFirst) {
        // Heavy full-resolution QR decode — run it OFF the main thread so a large
        // upload doesn't freeze the tab (see Scanner.scanPixelsHeavy / the worker).
        const scan = await this.scanner.scanPixelsHeavy(canvasToPixels(canvas)).catch(() => null);
        if (scan?.document && scan.document.parser !== 'text') {
          // The signature can't tell an export from a picture of one, so ALSO ask the
          // CNN what the image is. (PDFs skip it: the container already answers.) The
          // flow needs this to route — a picture of a CNH-e proceeds on the QR data,
          // while a photo of the physical card must collect front + back, which Veriff
          // approves.
          const source: 'pdf' | 'image' = isPdfUpload(file) ? 'pdf' : 'image';
          const cnnType = source === 'image' ? await this.classifyQuietly(dataUrl) : null;
          this.dbg('info', 'digital', `signed ${scan.document.kind} decoded from upload`, {
            kind: scan.document.kind,
            signature: scan.document.signature?.status,
            source,
            cnnType,
          });
          playConfirmationBeep();
          this.digitalRecognized.emit({
            doc: scan.document,
            dataUrl,
            source,
            cnnType,
            rawStorageBlob: this.pendingIntake?.storageBlob,
            rawStorageName: this.pendingIntake?.storageName,
            fileMetadata: this.pendingIntake?.metadata,
          });
          return;
        }
        this.dbg('info', 'digital', 'no signed QR in upload, using CNN');
      }

      const img = this.analysisImageData(canvas, canvas.width, canvas.height);
      const res = img ? analyzeDocument(img) : null;
      if (res) this.analysis = res;

      // The file is read — confirm it now, before the best-effort steps below.
      this.photo = dataUrl;
      this.status = 'captured';
      playConfirmationBeep();

      // OCR is best-effort: in some browsers the Tesseract wasm can fail to read
      // the image. That must not abort the upload or swallow the result — fall
      // back to no OCR text (the CNN still classifies straight from the image).
      let check: OcrCheck;
      let featImage: ImageData | null = null;
      try {
        ({ check, featImage } = await this.ocrUpright(dataUrl, res?.box ?? null));
      } catch {
        check = EMPTY_OCR_CHECK;
      }
      this.ocr = check;
      this.logOcr(check, 'OCR of uploaded file');
      await this.classifyAndShow(featImage, check.text, dataUrl);
    } catch (e) {
      this.error = String(e);
      this.dbg('error', 'upload', 'failed to process file', { error: String(e) });
    } finally {
      // Release the big rendered canvas so its backing memory can be reclaimed.
      if (bigCanvas) {
        bigCanvas.width = 0;
        bigCanvas.height = 0;
        bigCanvas = null;
      }
      this.identifying = false;
      try { this.cdr.detectChanges(); } catch {}
    }
  }

  /**
   * Identify a captured still that skipped the legibility gate (manual grab):
   * OCR it upright, then classify. Guarded so a stale result can't overwrite a
   * newer capture.
   */
  private async identify(dataUrl: string, box: Box | null): Promise<void> {
    let check: OcrCheck;
    let featImage: ImageData | null = null;
    try {
      ({ check, featImage } = await this.ocrUpright(dataUrl, box));
    } catch {
      check = { words: 0, goodWords: 0, confidence: 0, legible: true, text: '' };
    }
    this.logOcr(check, 'OCR of captured still');
    await this.classifyAndShow(featImage, check.text, dataUrl);
    try { this.cdr.detectChanges(); } catch {}
  }

  /**
   * Classify the upright feature image (via the trained model, falling back to
   * keywords inside the service) and show the result — unless a newer capture
   * has replaced this one.
   */
  /**
   * Downscale the classify input to {@link CLASSIFY_MAX_EDGE} so the synchronous,
   * main-thread on-device recognizer never runs on a multi-megapixel image (the
   * cause of the post-back-upload tab freeze). Returns the original when it's
   * already small enough; fails open to the original on any decode/encode error.
   */
  private async downscaledForClassify(dataUrl: string): Promise<string> {
    try {
      const img = new Image();
      img.src = dataUrl;
      await img.decode();
      const longEdge = Math.max(img.naturalWidth, img.naturalHeight);
      if (!longEdge || longEdge <= CLASSIFY_MAX_EDGE) return dataUrl;
      const scale = CLASSIFY_MAX_EDGE / longEdge;
      const w = Math.round(img.naturalWidth * scale);
      const h = Math.round(img.naturalHeight * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return dataUrl;
      ctx.drawImage(img, 0, 0, w, h);
      const out = canvas.toDataURL('image/jpeg', 0.9);
      canvas.width = canvas.height = 0; // release the scaled canvas promptly
      return out;
    } catch {
      return dataUrl;
    }
  }

  /**
   * Classify an image WITHOUT touching capture state or emitting anything — used on
   * the digital path, where a signed QR already decoded and we only need to know
   * *what the picture is* (a digital-CNH screen vs the physical card).
   *
   * Returns null when the on-device recognizer can't answer (heavy processing off,
   * models not warm, or a throw). Null is not "unknown document" — it is "we didn't
   * ask"; the flow treats it conservatively rather than guessing. Never blocks the
   * upload: a cold CNN would freeze the tab, so we only use it when already warm,
   * exactly as {@link classifyAndShow} does.
   */
  private async classifyQuietly(dataUrl: string): Promise<string | null> {
    if (!this.deviceCapability.heavyProcessingEnabled || !this.cnn.available) {
      this.prewarmHeavyModels();
      return null;
    }
    try {
      const classifyInput = await this.downscaledForClassify(dataUrl);
      await this.watchdog.setPhase('cnn_classify', {});
      const pred = await this.deviceCapability.guardCapture(() => this.cnn.classify(classifyInput));
      return pred?.type ?? null;
    } catch (e) {
      this.dbg('warn', 'classify', 'digital-path classify failed — provenance unknown', { error: String(e) });
      return null;
    }
  }

  private async classifyAndShow(featImage: ImageData | null, text: string, dataUrl: string): Promise<void> {
    this.identifying = true;
    try {
      // Primary: GPU-trained CNN on the captured image. Falls back to the
      // feature-fusion model, then keywords (both inside the services).
      // Heavy CNN (which runs the brdocs preprocess: localize + u2net + YuNet +
      // OSD) only when the device can run it within budget; otherwise fall
      // straight through to the cheap keyword classifier.
      let heavy: DocPrediction | null = null;
      if (this.deviceCapability.heavyProcessingEnabled && this.cnn.available) {
        // Only run the on-device recognizer when it is already WARM (models loaded).
        // A COLD run would build the onnx/WebGPU sessions synchronously on the main
        // thread (~10s) and freeze the tab — the confirmed post-upload stall. When
        // cold we skip it here and fall through to the cheap keyword classifier; the
        // background pre-warm (prewarmHeavyModels) makes it available for the next
        // side. Feed a size-capped image (the full-res `dataUrl` stays the QR/OCR/
        // storage source). Breadcrumb first so any stall is attributed to cnn_classify.
        const classifyInput = await this.downscaledForClassify(dataUrl);
        await this.watchdog.setPhase('cnn_classify', {});
        heavy = await this.deviceCapability.guardCapture(() => this.cnn.classify(classifyInput));
      } else if (this.deviceCapability.heavyProcessingEnabled && !this.cnn.available) {
        // Heavy processing is on but the models aren't warm yet — don't block on a
        // cold load; use the keyword classifier this time and keep warming.
        this.dbg('info', 'model', 'CNN not warm yet — keyword classify this time, warming in background');
        this.prewarmHeavyModels();
      }
      const pred = heavy ?? (await this.docModel.classify(featImage, text));
      if (this.photo === dataUrl) {
        this.docType = pred;
        const d = this.cnn.lastDebug;
        this.dbg('info', 'classify', `classified as ${pred.type}`, {
          type: pred.type,
          label: pred.label,
          confidence: pred.confidence,
          source: pred.source,
          ood: d?.ood,
          topProb: d ? Number(d.topProb?.toFixed?.(3)) : undefined,
        });
        this.emitConfirmed(pred, dataUrl, text);
      }
    } catch (e) {
      this.dbg('error', 'classify', 'classification failed', { error: String(e) });
      // #4: never dead-end. If the classifier/OCR models failed to load or threw,
      // still emit an `unknown`/unrecognized result (flagged) so the host advances
      // — its manual document-type picker / front-only routing takes over — rather
      // than leaving the user stuck on the capture screen with no event.
      if (this.photo === dataUrl) {
        const fallback = this.fallbackPrediction();
        this.docType = fallback;
        this.emitConfirmed(fallback, dataUrl, text, true);
      }
    } finally {
      this.identifying = false;
      try { this.cdr.detectChanges(); } catch {}
    }
  }

  /** A safe `unknown` prediction used when classification can't run (see #4). */
  private fallbackPrediction(): DocPrediction {
    return {
      type: 'unknown',
      label: this.translate.instant('DocCaptureUnknownType'),
      confidence: 'low',
      source: 'keywords',
    };
  }

  /** Emit the confirmed-document result (type + processed bytes) for consumers. */
  private emitConfirmed(
    pred: DocPrediction,
    dataUrl: string,
    ocrText: string,
    classifyFailed = false,
  ): void {
    // The rectified image fed to the model (model path); fall back to the raw
    // capture if the CNN path didn't run (keyword fallback).
    const processedDataUrl = this.cnn.lastDebug?.fed ?? dataUrl;
    this.documentConfirmed.emit({
      type: pred.type,
      label: pred.label,
      confidence: pred.confidence,
      recognized: pred.type !== 'unknown',
      processedDataUrl,
      processed: dataUrlToBytes(processedDataUrl),
      ocrText,
      rawDataUrl: dataUrl,
      rawStorageBlob: this.pendingIntake?.storageBlob,
      rawStorageName: this.pendingIntake?.storageName,
      fileMetadata: this.pendingIntake?.metadata,
      ...(classifyFailed ? { classifyFailed: true } : {}),
    });
    this.pendingIntake = null;
  }

  /** Compact per-expert vote summary for the model path (debug/transparency). */
  protected expertSummary(p: DocPrediction): string {
    return (p.experts ?? []).map((e) => `${e.modality}:${e.type}`).join('  ');
  }

  /** trackBy for the camera list. */
  protected trackByDeviceId(_index: number, cam: MediaDeviceInfo): string {
    return cam.deviceId;
  }

  /** trackBy for the CNN debug pipeline stages. */
  protected trackByStageName(_index: number, s: { name: string }): string {
    return s.name;
  }

  /** trackBy for the CNN debug rotation attempts. */
  protected trackByRotationDeg(_index: number, r: { deg: number }): number {
    return r.deg;
  }

  ngOnDestroy(): void {
    this.stop();
    void this.ocrWorker?.terminate();
  }
}

/** A flattened OCR word with just the fields we score on. */
interface OcrWord {
  text: string;
  confidence: number;
}

/** Flatten Tesseract's block → paragraph → line → word tree into a word list. */
function collectWords(
  blocks: { paragraphs: { lines: { words: OcrWord[] }[] }[] }[] | null,
): OcrWord[] {
  const out: OcrWord[] = [];
  for (const b of blocks ?? []) {
    for (const p of b.paragraphs ?? []) {
      for (const l of p.lines ?? []) {
        for (const w of l.words ?? []) {
          out.push({ text: w.text, confidence: w.confidence });
        }
      }
    }
  }
  return out;
}

/** Viewport hint text per detector state. */
const STATUS_TEXT: Record<Status, string> = {
  idle: 'Press Start to scan a document.',
  searching: 'Searching for a document…',
  'too-small': 'Move closer — text too small to read.',
  blurry: 'Hold still — focusing…',
  'not-document': 'Frame the document inside the view.',
  hold: 'Document detected — hold steady…',
  capturing: 'Capturing — hold steady…',
  verifying: 'Checking legibility…',
  captured: 'Captured!',
};

/**
 * Did this upload arrive as a PDF? The genuine CNH-e is exported from the official
 * app as a PDF, so the container is the one signal that separates a real export from
 * a photograph of one (the signed QR does not — both carry the same payload).
 * Checks the extension too: some Android pickers hand over an empty/wrong MIME type.
 */
function isPdfUpload(file: File): boolean {
  return file.type === 'application/pdf' || /\.pdf$/i.test(file.name ?? '');
}

/**
 * Decode a base64 data URL into raw bytes (for the processed-file output).
 * Never throws: a malformed data URL yields an empty array so the confirm emit
 * (including the #4 fallback) can't itself dead-end the flow.
 */
function dataUrlToBytes(dataUrl: string): Uint8Array {
  try {
    const comma = dataUrl.indexOf(',');
    const b64 = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return bytes;
  } catch {
    return new Uint8Array();
  }
}
